import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarScrollWrapper,
  SidebarSpace,
  SidebarSubmenu,
  SidebarSubmenuItem,
} from '@backstage/core-components';
import { NavContentBlueprint, type NavContentComponentProps } from '@backstage/plugin-app-react';
import { useApp } from '@backstage/core-plugin-api';
import { SidebarLogo } from './SidebarLogo';
import {
  RiMenuLine,
  RiSearchLine,
  RiHeartPulseLine,
  RiCalendarScheduleLine,
  RiHome3Line,
} from '@remixicon/react';
import { SidebarSearchModal } from '@backstage/plugin-search';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';

const SidebarNav = ({ navItems }: NavContentComponentProps) => {
  const app = useApp();
  const nav = navItems.withComponent(item => (
    <SidebarItem icon={() => item.icon} to={item.href} text={item.title} />
  ));

  // Skipped items — rendered manually below
  nav.take('page:search');
  nav.take('page:catalog');

  return (
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label="Search" icon={<RiSearchLine />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<RiMenuLine />}>
        {/* Catalog with entity-kind submenu */}
        <SidebarItem icon={() => <RiHome3Line />} to="/" text="Catalog">
          <SidebarSubmenu title="Catalog">
            <SidebarSubmenuItem
              title="Domains"
              to="/?filters[kind]=domain"
              icon={app.getSystemIcon('kind:domain')!}
            />
            <SidebarSubmenuItem
              title="Systems"
              to="/?filters[kind]=system"
              icon={app.getSystemIcon('kind:system')!}
            />
            <SidebarSubmenuItem
              title="Components"
              to="/?filters[kind]=component"
              icon={app.getSystemIcon('kind:component')!}
            />
            <SidebarSubmenuItem
              title="APIs"
              to="/?filters[kind]=api"
              icon={app.getSystemIcon('kind:api')!}
            />
            <SidebarDivider />
            <SidebarSubmenuItem
              title="Resources"
              to="/?filters[kind]=resource"
              icon={app.getSystemIcon('kind:resource')!}
            />
            <SidebarDivider />
            <SidebarSubmenuItem
              title="Groups"
              to="/?filters[kind]=group"
              icon={app.getSystemIcon('kind:group')!}
            />
            <SidebarSubmenuItem
              title="Users"
              to="/?filters[kind]=user"
              icon={app.getSystemIcon('kind:user')!}
            />
          </SidebarSubmenu>
        </SidebarItem>
        {nav.take('page:scaffolder')}
        <SidebarDivider />
        <SidebarScrollWrapper>
          <SidebarItem icon={() => <RiHeartPulseLine />} to="aws-health-monitor" text="AWS Health" />
          <SidebarItem icon={() => <RiCalendarScheduleLine />} to="ecs-scheduled-tasks" text="ECS Tasks" />
          {nav.rest({ sortBy: 'title' })}
        </SidebarScrollWrapper>
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      <NotificationsSidebarItem />
      <SidebarDivider />
      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        {nav.take('page:app-visualizer')}
        {nav.take('page:user-settings')}
      </SidebarGroup>
    </Sidebar>
  );
};

export const SidebarContent = NavContentBlueprint.make({
  params: {
    component: SidebarNav,
  },
});
